import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";

const CATEGORIES = ["About IndiaFast", "Domestic Courier", "International Courier", "USA", "UK", "Canada",
  "Australia", "New Zealand", "UAE", "Europe", "Food Shipping", "Packing", "Prohibited Items",
  "Tracking", "Pickup", "Payments", "Claims", "Business Shipping", "Franchise", "FAQs"];

const EMPTY = { category: "FAQs", title: "", body: "", keywords: "", enabled: true, priority: 0 };

export default function AiKnowledge() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/ai/knowledge").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load knowledge base"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = { ...form, keywords: String(form.keywords || "").split(",").map((k) => k.trim()).filter(Boolean), priority: Number(form.priority) || 0 };
    try {
      if (form.id) await api.put(`/admin/ai/knowledge/${form.id}`, payload);
      else await api.post("/admin/ai/knowledge", payload);
      toast.success("Saved");
      setForm(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this knowledge entry?")) return;
    try { await api.delete(`/admin/ai/knowledge/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggle = async (item) => {
    try {
      await api.put(`/admin/ai/knowledge/${item.id}`, { ...item, keywords: item.keywords || [], enabled: !item.enabled });
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="p-4 sm:p-6" data-testid="ai-knowledge-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold text-white">AI Knowledge Base</h1>
          <p className="text-sm text-slate-400">The assistant answers ONLY from these approved entries and published FAQs.</p>
        </div>
        <button onClick={() => setForm({ ...EMPTY })} data-testid="ai-kb-add-btn"
                className="flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover">
          <Plus className="h-4 w-4" /> Add Entry
        </button>
      </div>

      {form && (
        <div className="mb-4 space-y-2 rounded-xl border border-saffron/40 bg-admincard p-4" data-testid="ai-kb-form">
          <div className="grid gap-2 sm:grid-cols-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="ai-kb-category"
                    className="rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-sm text-slate-200">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="Title" data-testid="ai-kb-title"
                   className="rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-sm text-slate-200 outline-none focus:border-saffron" />
            <input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                   placeholder="Keywords (comma separated)" data-testid="ai-kb-keywords"
                   className="rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-sm text-slate-200 outline-none focus:border-saffron" />
          </div>
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={4}
                    placeholder="Approved answer text — the assistant will use this verbatim as its source." data-testid="ai-kb-body"
                    className="w-full rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-sm text-slate-200 outline-none focus:border-saffron" />
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                     data-testid="ai-kb-priority"
                     className="w-16 rounded-lg border border-adminborder bg-adminbg px-2 py-1 text-xs text-slate-200" />
              Priority (lower = preferred)
            </label>
            <button onClick={save} data-testid="ai-kb-save-btn"
                    className="rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover">Save</button>
            <button onClick={() => setForm(null)} data-testid="ai-kb-cancel-btn"
                    className="rounded-lg border border-adminborder px-4 py-2 text-xs font-bold text-slate-300 hover:bg-white/5">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="ai-kb-list">
        {items.map((it) => (
          <div key={it.id} className={`rounded-xl border bg-admincard p-4 ${it.enabled ? "border-adminborder" : "border-adminborder opacity-50"}`}
               data-testid={`ai-kb-item-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-bold text-white">
                  <span className="mr-2 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold text-saffron">{it.category}</span>
                  {it.title}
                </p>
                <p className="mt-1 whitespace-pre-line text-xs leading-relaxed text-slate-400">{it.body}</p>
                {(it.keywords || []).length > 0 && (
                  <p className="mt-1 text-[10px] text-slate-500">keywords: {it.keywords.join(", ")}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <button onClick={() => toggle(it)} data-testid={`ai-kb-toggle-${it.id}`}
                        className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${it.enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>
                  {it.enabled ? "Enabled" : "Disabled"}
                </button>
                <button onClick={() => setForm({ ...it, keywords: (it.keywords || []).join(", ") })} data-testid={`ai-kb-edit-${it.id}`}
                        className="rounded-lg border border-adminborder p-1.5 text-slate-300 hover:bg-white/5"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => remove(it.id)} data-testid={`ai-kb-delete-${it.id}`}
                        className="rounded-lg border border-adminborder p-1.5 text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-500">No entries yet.</p>}
      </div>
    </div>
  );
}
